import { EventDetailsDialog } from '@/features/calendar/components/dialogs/event-details-dialog'
import type { ICalendarEvent } from '@/features/calendar/interfaces'
import { cn } from '@/lib/utils'
import { differenceInCalendarDays, endOfDay, endOfWeek, isSameDay, parseISO, startOfDay, startOfWeek } from 'date-fns'

type Props = {
  weekStart: Date
  weekEnd: Date
  trainings: ICalendarEvent[]
  trainingPositions: Record<string, number>
}

const colorClass: Record<string, string> = {
    blue: 'border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-100',
    green: 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-100',
    red: 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-100',
    yellow: 'border-yellow-200 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-100',
    purple: 'border-purple-200 bg-purple-50 dark:border-purple-800 dark:bg-purple-100',
    orange: 'border-orange-200 bg-orange-50 dark:border-orange-800 dark:bg-orange-100',
}

export function WeekSpansRow({ weekStart, weekEnd, trainings, trainingPositions }: Props) {
  const wStart = startOfWeek(weekStart)
  const wEnd = endOfWeek(weekEnd)

  const spans = trainings
    .filter((t) => {
      const s = startOfDay(parseISO(t.startAt))
      const e = endOfDay(parseISO(t.endAt))
      return s <= wEnd && e >= wStart
    })
    .map((t) => {
      const s = startOfDay(parseISO(t.startAt))
      const e = endOfDay(parseISO(t.endAt))
      const segStart = s < wStart ? wStart : s
      const segEnd = e > wEnd ? wEnd : e
      const startCol = segStart.getDay() // 0..6 Sun..Sat
      const spanCols = differenceInCalendarDays(segEnd, segStart) + 1
      const lane = trainingPositions[t.id] ?? 0
      const roundLeft = isSameDay(segStart, s)
      const roundRight = isSameDay(segEnd, e)

      // Calculate Y position based on start time (timeline positioning)
      const startTime = parseISO(t.startAt)
      const startHour = startTime.getHours()
      const startMinutes = startTime.getMinutes()
      const topPosition = (startHour * 96) + (startMinutes * 96 / 60) // 96px per hour

      return { training: t, startCol, spanCols, lane, roundLeft, roundRight, topPosition }
    })

  if (spans.length === 0)
    return null

  return (
    <div className="pointer-events-none absolute left-0 right-0 top-0 h-full">
      {spans.map(({ training, startCol, spanCols, lane, roundLeft, roundRight, topPosition }) => (
        <EventDetailsDialog
          key={`${training.id}-${startCol}`}
          training={training}
        >
          <div
            className={cn(
              'pointer-events-auto absolute flex h-auto items-center justify-between truncate whitespace-nowrap rounded-md border p-2 text-xs z-10',
              colorClass[training.color] ?? colorClass.blue,
              roundLeft ? 'rounded-l-md' : 'rounded-l-none',
              roundRight ? 'rounded-r-md' : 'rounded-r-none',
            )}
            // eslint-disable-next-line react/forbid-dom-props
            style={{
              left: `${(startCol / 7) * 100}%`,
              width: `${(spanCols / 7) * 100}%`,
              top: `${topPosition + (lane * 28)}px`
            }}
          >
            <p className="flex-1 whitespace-pre-wrap font-semibold">{training.title}</p>
            <span>{new Date(training.startAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - {new Date(training.endAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
          </div>
        </EventDetailsDialog>
      ))}
    </div>
  )
}
